import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { staffService, expenseService, Staff, Expense } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Plus,
  Star,
  Wallet,
  IndianRupee,
  Loader2,
  CheckCircle2,
  Trash2,
  MapPin,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

export default function AdminSpecialStaffPage() {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<Staff | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    remark: '',
    km: '',
  });

  useEffect(() => {
    loadData();
  }, [staffId]);

  const loadData = async () => {
    if (!staffId) return;
    try {
      const [staffData, expensesData] = await Promise.all([
        staffService.getById(staffId),
        expenseService.getAll({ staffId }),
      ]);
      setStaff(staffData);
      setExpenses(expensesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const perKmRate = staff?.bikeRatePerKm || 0;

  const handleKmChange = (val: string) => {
    const kmVal = val;
    if (kmVal && !isNaN(parseFloat(kmVal)) && parseFloat(kmVal) > 0) {
      const calc = perKmRate > 0 ? (parseFloat(kmVal) * perKmRate).toFixed(2) : '';
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: calc
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: ''
      }));
    }
  };

  const handleAddTravelAllowance = async () => {
    if (!staffId || !staff) return;
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    let finalRemark = formData.remark || 'Travel Allowance';
    if (formData.km && parseFloat(formData.km) > 0) {
      const kmInfo = `${formData.km} KM @ ₹${perKmRate}/km`;
      if (!finalRemark.includes(kmInfo)) {
        finalRemark = `${finalRemark} (${kmInfo})`;
      }
    }

    setSubmitting(true);
    try {
      await expenseService.create({
        staffId,
        staffName: staff.name,
        date: formData.date,
        amount: parseFloat(formData.amount),
        remark: finalRemark,
        category: 'Travel Allowance',
      });
      // Auto-approve
      const allExpenses = await expenseService.getAll({ staffId });
      const newest = allExpenses.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())[0];
      if (newest && newest.status === 'pending') {
        await expenseService.updateStatus(newest.id, 'approved', 'Auto-approved: Travel Allowance');
      }
      toast({ title: 'Travel Allowance added and approved' });
      setDialogOpen(false);
      setFormData({ date: new Date().toISOString().split('T')[0], amount: '', remark: '', km: '' });
      await loadData();
    } catch (error) {
      toast({ title: 'Failed to add travel allowance', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    try {
      await expenseService.delete(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const approvedTotal = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);
  const pendingTotal = expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Staff not found</p>
        <Button variant="ghost" onClick={() => navigate('/admin/staff')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Staff
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/staff')} className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{staff.name}</h1>
              <Star className="w-5 h-5 text-purple-500 fill-purple-500" />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Special Staff — Travel Allowance Panel</p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl">
          <MapPin className="w-4 h-4 mr-1" /> Add Travel Allowance
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white/50 dark:bg-slate-900/40 border-emerald-200/50 dark:border-emerald-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/10">
              <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">₹{(staff.walletBalance || 0).toLocaleString('en-IN')}</p>
              <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Wallet Balance</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-purple-200/50 dark:border-purple-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/10">
              <CheckCircle2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-black text-purple-600 dark:text-purple-400">₹{approvedTotal.toLocaleString('en-IN')}</p>
              <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-amber-200/50 dark:border-amber-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/10">
              <IndianRupee className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-black text-amber-600 dark:text-amber-400">₹{pendingTotal.toLocaleString('en-IN')}</p>
              <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pending</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/50 dark:bg-slate-900/40">
        <CardContent className="p-0">
          {expenses.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No expenses yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Click "Add Travel Allowance" to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-medium">
                      {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide',
                        exp.category === 'Travel Allowance'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      )}>
                        {exp.category || 'Other Exp'}
                      </span>
                    </TableCell>
                    <TableCell className="font-bold">₹{exp.amount.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 dark:text-slate-400">{exp.remark || '-'}</TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide',
                        exp.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                        exp.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                      )}>
                        {exp.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(exp.id)}
                        className="h-7 px-2 text-[10px] font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Travel Allowance</DialogTitle>
            <DialogDescription>
              Add a travel allowance expense for {staff.name}. This will be auto-approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="p-3.5 rounded-2xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-purple-600" />
                  Travel Allowance Options
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                  Rate: ₹{perKmRate}/km
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Kilometers (KM)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="e.g. 15"
                    value={formData.km}
                    onChange={(e) => handleKmChange(e.target.value)}
                    className="h-9 rounded-xl border-purple-200 dark:border-purple-900/50 bg-white dark:bg-slate-900"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    disabled={!!formData.km && parseFloat(formData.km) > 0}
                    className="h-9 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                  />
                </div>
              </div>

              {formData.km && parseFloat(formData.km) > 0 ? (
                <p className="text-[11px] font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                  ✓ {formData.km} KM × ₹{perKmRate}/km = ₹{formData.amount} (Amount section auto-calculated & disabled)
                </p>
              ) : (
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Fill KM to auto-calculate amount, or enter Amount directly for manual travel claim.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Remarks</Label>
              <Textarea
                placeholder="Travel allowance details..."
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTravelAllowance} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Add & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
